/**
 * RegionalExportAdditionalCharts.jsx
 * Standalone exportable chart components for Regional By-Program and Chord diagrams.
 * One pair of components per region, suitable for chart registry & ExportRenderer.
 *
 * Uses the same region-matching logic as RegionalFocusSection.jsx:
 *   1. Check regionsParsed (pre-parsed array) first
 *   2. Fall back to parsing raw regions string by splitting on newlines
 *      and stripping leading dash/bullet prefixes
 *   3. Exact set matching against REGIONS[].match
 *   4. Country-based fallback via deriveSubRegionsFromCountries
 */

import { useMemo } from 'react';
import { useApp } from '../../../context/AppContext';
import RegionalByProgramChart from './RegionalByProgramChart';
import ImpactAreaCrossProgramChord from './ImpactAreaCrossProgramChord';
import RegionalByTypeChart from './RegionalByTypeChart';
import RegionalFundingChart from './RegionalFundingChart';
import RegionalCrossProgramHeatmap from './RegionalCrossProgramHeatmap';
import RegionalImpactAreaChart from './RegionalImpactAreaChart';
import { deriveSubRegionsFromCountries } from '../../../utils/countryMapping';

const REGIONS = [
  {
    key: 'africa',
    label: 'Africa',
    color: '#F37021',
    match: [
      'Africa',
      'Western Africa',
      'Eastern Africa',
      'Middle Africa',
      'Northern Africa',
      'Southern Africa',
      'Sub-Saharan Africa',
    ],
  },
  {
    key: 'americas',
    label: 'The Americas',
    color: '#007DBC',
    match: [
      'Americas',
      'The Americas',
      'Caribbean',
      'Central America',
      'South America',
      'Northern America',
      'Latin America and the Caribbean',
    ],
  },
  {
    key: 'asia',
    label: 'Asia',
    color: '#4CAF50',
    match: [
      'Asia',
      'Southern Asia',
      'South-Eastern Asia',
      'Eastern Asia',
      'Central Asia',
      'Western Asia',
    ],
  },
];

/**
 * Parse a raw regions string into an array of region names.
 * Replicates the logic from RegionalFocusSection.jsx.
 */
function parseRegionString(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return raw
    .split(/\r?\n/)
    .map(s => s.replace(/^[-\u2013\u2022\s]+/, '').trim())
    .filter(Boolean);
}

/**
 * Returns true when a result belongs to the target region.
 * Replicates the logic from RegionalFocusSection.jsx.
 */
function resultInRegion(result, matchList) {
  const matchSet = new Set(matchList);

  const regionList =
    (Array.isArray(result.regionsParsed) && result.regionsParsed.length > 0)
      ? result.regionsParsed
      : parseRegionString(result.regions);

  if (regionList.length > 0) {
    if (regionList.some(r => matchSet.has(r))) return true;
  }

  // Fallback: derive sub-regions from countries when regions is missing/empty
  if (regionList.length === 0) {
    const countries = result.countriesParsed || result.countries;
    if (countries) {
      const derived = deriveSubRegionsFromCountries(countries);
      for (const sr of derived) {
        if (matchSet.has(sr)) return true;
      }
    }
  }

  return false;
}

/** Factory: creates a standalone By-Program chart component for a given region. */
function createRegionByProgramChart(regionKey) {
  const region = REGIONS.find(r => r.key === regionKey);

  return function RegionByProgramExportChart() {
    const { filteredResults } = useApp();

    const regionResults = useMemo(
      () => filteredResults.filter(r => resultInRegion(r, region.match)),
      [filteredResults],
    );

    if (!region) return null;
    return <RegionalByProgramChart results={regionResults} color={region.color} />;
  };
}

/** Factory: creates a standalone Chord diagram component for a given region. */
function createRegionChordChart(regionKey) {
  const region = REGIONS.find(r => r.key === regionKey);

  return function RegionChordExportChart() {
    const { filteredResults } = useApp();

    const regionResults = useMemo(
      () => filteredResults.filter(r => resultInRegion(r, region.match)),
      [filteredResults],
    );

    if (!region) return null;
    return <ImpactAreaCrossProgramChord results={regionResults} color={region.color} />;
  };
}

// By Program (3)
export const AfricaByProgramChart   = createRegionByProgramChart('africa');
export const AmericasByProgramChart  = createRegionByProgramChart('americas');
export const AsiaByProgramChart      = createRegionByProgramChart('asia');

// Chord (3)
export const AfricaChordChart   = createRegionChordChart('africa');
export const AmericasChordChart  = createRegionChordChart('americas');
export const AsiaChordChart      = createRegionChordChart('asia');

/** Factory: creates a standalone By-Type chart component for a given region. */
function createRegionByTypeChart(regionKey) {
  const region = REGIONS.find(r => r.key === regionKey);

  return function RegionByTypeExportChart() {
    const { filteredResults } = useApp();

    const regionResults = useMemo(
      () => filteredResults.filter(r => resultInRegion(r, region.match)),
      [filteredResults],
    );

    if (!region) return null;
    return <RegionalByTypeChart results={regionResults} color={region.color} />;
  };
}

/** Factory: creates a standalone Funding Split chart component for a given region. */
function createRegionFundingChart(regionKey) {
  const region = REGIONS.find(r => r.key === regionKey);

  return function RegionFundingExportChart() {
    const { filteredResults } = useApp();

    const regionResults = useMemo(
      () => filteredResults.filter(r => resultInRegion(r, region.match)),
      [filteredResults],
    );

    if (!region) return null;
    return <RegionalFundingChart results={regionResults} color={region.color} />;
  };
}

/** Factory: creates a standalone Cross-Program Heatmap component for a given region. */
function createRegionHeatmapChart(regionKey) {
  const region = REGIONS.find(r => r.key === regionKey);

  return function RegionHeatmapExportChart() {
    const { filteredResults } = useApp();

    const regionResults = useMemo(
      () => filteredResults.filter(r => resultInRegion(r, region.match)),
      [filteredResults],
    );

    if (!region) return null;
    return <RegionalCrossProgramHeatmap results={regionResults} color={region.color} />;
  };
}

/** Factory: creates a standalone Impact Area Coverage chart component for a given region. */
function createRegionImpactAreaChart(regionKey) {
  const region = REGIONS.find(r => r.key === regionKey);

  return function RegionImpactAreaExportChart() {
    const { filteredResults } = useApp();

    const regionResults = useMemo(
      () => filteredResults.filter(r => resultInRegion(r, region.match)),
      [filteredResults],
    );

    if (!region) return null;
    return <RegionalImpactAreaChart results={regionResults} color={region.color} />;
  };
}

// By Type (3)
export const AfricaByTypeChart   = createRegionByTypeChart('africa');
export const AmericasByTypeChart  = createRegionByTypeChart('americas');
export const AsiaByTypeChart      = createRegionByTypeChart('asia');

// Funding Split (3)
export const AfricaFundingChart   = createRegionFundingChart('africa');
export const AmericasFundingChart  = createRegionFundingChart('americas');
export const AsiaFundingChart      = createRegionFundingChart('asia');

// Cross-Program Heatmap (3)
export const AfricaHeatmapChart   = createRegionHeatmapChart('africa');
export const AmericasHeatmapChart  = createRegionHeatmapChart('americas');
export const AsiaHeatmapChart      = createRegionHeatmapChart('asia');

// Impact Area Coverage (3)
export const AfricaImpactAreaChart   = createRegionImpactAreaChart('africa');
export const AmericasImpactAreaChart  = createRegionImpactAreaChart('americas');
export const AsiaImpactAreaChart      = createRegionImpactAreaChart('asia');
